//-- Copyright (c) 2021 TMT Insights Group, LLC
import { useRef } from 'react';
import DOMPurify from 'dompurify';
import styled from '@emotion/styled';
import { NotificationRecord, NotificationVisibility, changeNotificationSeenState, getNotifications } from '@/redux/slices/notificationSlice';
import { selectNotificationPagination, selectNotifications, selectNotificationsLoading } from '@/redux/selectors/notificationSelectors';
import { Waypoint } from 'react-waypoint';
import { getAgeFromDate } from '@/_utils/date';
import { CircularProgress, Fade, Grid, lighten, Slide, Typography, Tooltip } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import NoNotificationsSvg from '@/assets/images/no-notifications.svg';
import { useNotificationNavigation } from '@/components/_hooks/useNotificationNavigation';

interface NotificationListProps {
  showAll?: boolean;
  type: string;
  onClose: () => void;
}

const NotificationList = ({ showAll, type, onClose }: NotificationListProps) => {
  const dispatch = useAppDispatch();
  const { handleNotificationNavigation } = useNotificationNavigation();
  const notificationsRecords = useAppSelector(selectNotifications(!!showAll, type));
  const visibility = showAll === true ? NotificationVisibility.ALL : NotificationVisibility.UNSEEN;
  const pagination = useAppSelector(selectNotificationPagination(visibility, type));
  const loading = useAppSelector(selectNotificationsLoading);
  const isFetching = useRef(false);

  const onFetchData = (page: number) => {
    const seen = !showAll ? false : undefined;
    isFetching.current = true;
    dispatch(getNotifications({ page, group_type: type, seen }))
      .unwrap()
      .then(() => {
        isFetching.current = false;
      });
  };

  const onFetchMoreData = () => {
    if (!loading && !isFetching.current && notificationsRecords.length < pagination.total) {
      onFetchData((pagination.page ?? 0) + 1);
    }
  };

  const onMarkSeen = (id: number, group_type: string, e?: React.MouseEvent<HTMLDivElement>) => {
    e?.stopPropagation();
    const payload = {
      ids: [id],
      group_type,
      showAll,
      seen: true
    };

    dispatch(changeNotificationSeenState(payload));
  };

  const onClickNotification = (notif: NotificationRecord) => {
    onMarkSeen(notif.id, notif.notification.group_type);
    handleNotificationNavigation(notif);
    onClose();
  };

  const getNotificationText = (record: NotificationRecord) => {
    const message = record.notification.message;
    const __html = DOMPurify.sanitize(message, { USE_PROFILES: { html: true } });

    return <div dangerouslySetInnerHTML={{ __html }} />;
  };

  const items = notificationsRecords.map((record, index) => {
    const timing = getAgeFromDate(record.created_at);
    const message = getNotificationText(record);
    const pageNum = Math.floor(index / 20);
    const base = index - 20 * pageNum;

    return (
      <Slide in direction="left" timeout={(base + 1) * 100} key={record.id}>
        <NotificationWrapper>
          <Fade in timeout={(base + 1) * 200} key={record.id}>
            <NotificationItem onClick={() => onClickNotification(record)} item xs={12} key={record.id}>
              <Grid container alignItems="center">
                <Grid item>
                  <NotificationMessage>{message}</NotificationMessage>
                  <Timing>{timing === 'Just now' ? timing : `${timing} ago`}</Timing>
                </Grid>
                {!record.seen && (
                  <Tooltip title="Mark as read" arrow>
                    <DotContainer onClick={(e) => onMarkSeen(record.id, record.notification.group_type, e)}>
                      <Dot />
                    </DotContainer>
                  </Tooltip>
                )}
              </Grid>
            </NotificationItem>
          </Fade>
        </NotificationWrapper>
      </Slide>
    );
  });

  return (
    <Wrapper container>
      {items}
      {!!items.length && <Waypoint onEnter={onFetchMoreData} />}
      {loading ? (
        <NotificationItemLoader transparent={!items.length}>
          <CircularProgress />
        </NotificationItemLoader>
      ) : !items.length ? (
        <Grid container sx={{ py: 3 }} flexDirection="column" alignItems="center" justifyContent="center ">
          <NoNotificationImage src={NoNotificationsSvg} alt="No notifications" />
          <Typography variant="caption" fontWeight="normal" fontStyle="italic">
            No Notifications
          </Typography>
        </Grid>
      ) : null}
    </Wrapper>
  );
};

export default NotificationList;

const Wrapper = styled(Grid)`
  max-height: 530px;
  overflow-x: hidden;
  overflow-y: auto;

  .infinite-scroll-component__outerdiv {
    width: 100%;
  }
`;

const NotificationWrapper = styled.div`
  width: 100%;
  border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
`;

const NotificationItem = styled(Grid)`
  padding: ${(props) => props.theme.spacing(1.25, 2)};
  font-size: 0.875rem;
  display: flex;
  justify-content: center;
  flex-direction: column;
  line-height: 1.25;
  background-color: ${({ theme }) => lighten(theme.palette.background.paper, 0.06)};
  transition: ease background-color 250ms;
  &:hover {
    background-color: ${({ theme }) => lighten(theme.palette.background.paper, 0.07)};
    transition: ease background-color 250ms;
    cursor: pointer;
  }
  &:first-of-type {
    border-top: 0;
  }

  &:last-child {
    border-bottom: 0;
  }
`;

const NotificationItemLoader = styled(NotificationItem, {
  shouldForwardProp: (prop) => 'transparent' !== prop
})<{ transparent: boolean }>`
  width: 100%;
  align-items: center;

  ${(props) =>
    props.transparent &&
    `
    background-color: transparent;
  `};
`;

const NotificationMessage = styled.div`
  div {
    display: inline-block;
    span {
      font-weight: ${({ theme }) => theme.typography.fontWeightBold};
    }
    span:before {
      display: none;
    }
  }
  a {
    color: inherit;
    text-decoration: none;
  }
`;
const DotContainer = styled.div`
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  cursor: pointer;
`;

const Dot = styled.div`
  background-color: ${({ theme }) => theme.palette.primary.main};
  width: 6px;
  height: 6px;
  border-radius: 6px;
`;

const Timing = styled.div`
  font-size: 0.785rem;
  font-style: italic;
  opacity: 0.5;
  margin-top: ${({ theme }) => theme.spacing(0.5)};
`;

const NoNotificationImage = styled.img`
  height: 140px;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;
